import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

function candidateDrop(row) {
  return row.caida_pct ?? row.caida ?? row.drop_pct ?? row.descenso_pct;
}

export default function ReindexadoPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reindexado/candidatos');
      setItems(data.items || []);
      setSelected({});
      setResults({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los candidatos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chosen = Object.entries(selected).filter(([, value]) => value).map(([id]) => id);
  const toggle = item => setSelected(current => ({ ...current, [item.item_id]: !current[item.item_id] }));
  const toggleAll = () => {
    const selectable = items.filter(item => !item.sin_publicacion && item.item_id);
    const everySelected = selectable.length && selectable.every(item => selected[item.item_id]);
    setSelected(Object.fromEntries(selectable.map(item => [item.item_id, !everySelected])));
  };

  const apply = async () => {
    if (!chosen.length) return toast.error('Selecciona al menos una publicación');
    setApplying(true);
    try {
      const { data } = await api.post('/reindexado/aplicar', { item_ids: chosen });
      const byId = Object.fromEntries((data.items || []).map(result => [result.item_id, result]));
      setResults(byId);
      const failed = (data.items || []).filter(result => !result.ok).length;
      if (failed) toast.error(`${failed} publicación(es) terminaron con error`);
      else toast.success(`${chosen.length} publicación(es) procesadas`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar el reindexado');
    } finally {
      setApplying(false);
    }
  };

  return (
    <DashboardLayout title="Reindexado ML" subtitle="Revisa publicaciones con caída de desempeño" icon={RefreshCw}>
      <div className="mb-5 border border-amber-500/30 bg-amber-500/10 rounded-xl px-4 py-3 flex gap-3 text-amber-100">
        <AlertTriangle className="shrink-0 text-amber-400 mt-0.5" size={19} />
        <p className="text-sm leading-6">Acción experimental: se vuelve a guardar la misma descripción y se intercambian las fotos 2↔3 y 4↔5. Su efecto en el algoritmo de Mercado Libre no está confirmado.</p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-4 border-b border-slate-800">
          <p className="text-sm text-slate-400">{items.length} candidato(s) de PriceHub · {chosen.length} seleccionado(s)</p>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading || applying} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="Actualizar"><RefreshCw size={17} className={loading ? 'animate-spin' : ''} /></button>
            <button onClick={apply} disabled={!chosen.length || applying} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 text-white px-3 py-2 rounded-lg text-sm font-medium">
              <RefreshCw size={16} className={applying ? 'animate-spin' : ''} /> Aplicar seleccionados
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wide">
              <tr><th className="p-3 text-left"><input aria-label="Seleccionar todos" type="checkbox" onChange={toggleAll} checked={Boolean(items.filter(i => !i.sin_publicacion).length) && items.filter(i => !i.sin_publicacion).every(i => selected[i.item_id])} /></th><th className="p-3 text-left">Publicación</th><th className="p-3 text-left">SKU</th><th className="p-3 text-left">Tienda / canal</th><th className="p-3 text-left">Caída</th><th className="p-3 text-left">Resultado</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? <tr><td colSpan="6" className="p-10 text-center text-slate-500">Cargando candidatos…</td></tr> : items.length === 0 ? <tr><td colSpan="6" className="p-10 text-center text-slate-500">No hay candidatos para reindexar.</td></tr> : items.map((item, index) => {
                const result = results[item.item_id];
                const error = result?.errores?.join(' · ');
                return <tr key={`${item.sku || 'sin-sku'}-${item.item_id || index}`} className="text-slate-300 hover:bg-slate-800/30">
                  <td className="p-3"><input aria-label={`Seleccionar ${item.sku || item.item_id}`} type="checkbox" checked={Boolean(selected[item.item_id])} onChange={() => toggle(item)} disabled={item.sin_publicacion || !item.item_id} title={item.sin_publicacion ? 'Este SKU no tiene publicación vinculada' : ''} /></td>
                  <td className="p-3"><div className="flex items-center gap-3">{item.imagen ? <img src={item.imagen} alt="" className="w-9 h-9 object-cover rounded-md bg-slate-800" /> : <div className="w-9 h-9 rounded-md bg-slate-800" />}<div><p className="font-medium text-slate-200">{item.title || item.item_id || 'Sin publicación'}</p>{item.sin_publicacion && <p className="text-xs text-amber-400">Sin publicación vinculada</p>}</div></div></td>
                  <td className="p-3 font-mono text-xs">{item.sku || '—'}</td><td className="p-3">{item.tienda || item.canal || '—'}</td><td className="p-3">{candidateDrop(item) ?? '—'}{candidateDrop(item) != null && '%'}</td>
                  <td className="p-3">{result && (result.ok ? <span className="inline-flex gap-1 text-emerald-400"><CheckCircle2 size={16} /> Listo</span> : <span title={error} className="inline-flex gap-1 text-red-400"><XCircle size={16} /> {error || 'Error'}</span>)}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
